/**
 * String tools.
 *
 * @module string
 * @license Apache-2.0
 * @author drmats
 */
import { draw, head, last, range, tail } from "./array";
import { Y } from "./func";
export var asciiLetters = function asciiLetters() {
  return "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
};
export var asciiLowercase = function asciiLowercase() {
  return "abcdefghijklmnopqrstuvwxyz";
};
export var asciiUppercase = function asciiUppercase() {
  return "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
};
export var big = Y(function (r) {
  return function (n) {
    var c = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "x";
    return n > 0 ? r(n - 1, c + c) : c;
  };
});
export var camelToPascal = function camelToPascal(str) {
  return str && str.length > 0 ? head(str).toUpperCase() + tail(str) : empty();
};
export var camelToSnake = function camelToSnake(str) {
  return str ? str.replace(/([A-Z])/g, "_$1").toLowerCase() : empty();
};
export var capitalize = function capitalize(str) {
  return str && str.length > 0 ? head(str).toUpperCase() + tail(str).toLowerCase() : empty();
};
export var digits = function digits() {
  return "0123456789";
};
var position = Object.freeze({
  BEGIN: 0,
  MIDDLE: 1,
  END: 2
});
export var ellipsis = function ellipsis(str) {
  var placing = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  var sep = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "…";
  var x = str.split(empty());

  if (str.length >= sep.length) {
    if (placing === position.MIDDLE) {
      x.splice(Math.floor(x.length / 2) - Math.floor(sep.length / 2), sep.length, sep);
    } else if (placing === position.BEGIN) {
      x.splice(0, sep.length, sep);
    } else if (placing === position.END) {
      x.splice(x.length - sep.length, sep.length, sep);
    }
  }

  return x.join(empty());
};
Object.freeze(Object.assign(ellipsis, position));
export var empty = function empty() {
  return "";
};
export var space = function space() {
  return " ";
};
export var nl = function nl() {
  return "\n";
};
export var tab = function tab() {
  return "\t";
};
export var padLeft = function padLeft() {
  var input = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty();
  var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var ch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : space();
  return function (ilen, c) {
    return range(len - ilen).map(function () {
      return c;
    }).join(empty()) + input;
  }(input.length, head(ch));
};
export var padRight = function padRight() {
  var input = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty();
  var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var ch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : space();
  return function (ilen, c) {
    return input + range(len - ilen).map(function () {
      return c;
    }).join(empty());
  }(input.length, head(ch));
};
export var pascalToCamel = function pascalToCamel(str) {
  return str && str.length > 0 ? head(str).toLowerCase() + tail(str) : empty();
};
export var pascalToSnake = function pascalToSnake(str) {
  return str ? tail(camelToSnake(str)) : empty();
};
export var quote = function quote() {
  var str = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty();
  var q = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "\"\"";
  return "".concat(head(q)).concat(str).concat(last(q));
};
export var random = function random() {
  var size = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var letters = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : asciiLetters() + digits();
  return range(size).map(function () {
    return draw(letters.split(empty()));
  }).join(empty());
};
export var shorten = function shorten(str) {
  var len = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Infinity;
  var placing = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var sep = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "…";
  var x = str.split(empty());

  if (len < str.length) {
    if (placing === position.MIDDLE) {
      x.splice(Math.floor(len / 2), str.length - len);
    } else if (placing === position.BEGIN) {
      x.splice(0, x.length - len);
    } else if (placing === position.END) {
      x.splice(len, x.length - len);
    }

    return ellipsis(x.join(empty()), placing, sep);
  }

  return str;
};
Object.freeze(Object.assign(shorten, position));
export var snakeToCamel = function snakeToCamel(str) {
  return function (pascal) {
    return pascal.length > 0 ? head(pascal).toLowerCase() + tail(pascal) : empty();
  }(snakeToPascal(str));
};
export var snakeToPascal = function snakeToPascal(str) {
  return str ? str.split(/_+/g).map(function (w) {
    return capitalize(w);
  }).join(empty()) : empty();
};
export var wrap = function wrap() {
  var str = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty();
  var prefix = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : empty();
  var suffix = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : empty();
  return "".concat(prefix).concat(str).concat(suffix);
};